import type { Caption } from '@remotion/captions';

/**
 * Transcripts survive a reload.
 *
 * A transcript is minutes of GPU time and cannot be regenerated from anything
 * else on the page, so losing one to an accidental refresh is the most
 * expensive mistake this app allows. Every operation here fails soft —
 * IndexedDB is unavailable in some private-browsing modes, and persistence is
 * a convenience, never a precondition for captioning a video.
 */

// Deliberately not renamed with the app: this key addresses data already on
// users' machines, and changing it would orphan every saved transcript.
const DB_NAME = 'subtitler';
const DB_VERSION = 1;
const STORE = 'transcripts';

export type SavedTranscript = {
  key: string;
  captions: Caption[];
  savedAt: number;
  fileName: string;
};

/** Identity of a file's *contents*, so the same clip re-picked still matches. */
export const fileKey = (file: File): string =>
  `${file.name}:${file.size}:${file.lastModified}`;

const openDb = (): Promise<IDBDatabase | null> =>
  new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null);
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      return resolve(null);
    }
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });

const withStore = async <T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest,
): Promise<T | null> => {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const request = fn(tx.objectStore(STORE));
      request.onsuccess = () => resolve((request.result as T) ?? null);
      request.onerror = () => resolve(null);
      tx.oncomplete = () => db.close();
    } catch {
      resolve(null);
    }
  });
};

export const loadTranscript = (key: string): Promise<SavedTranscript | null> =>
  withStore<SavedTranscript>('readonly', (store) => store.get(key));

export const saveTranscript = (entry: SavedTranscript): Promise<unknown> =>
  withStore('readwrite', (store) => store.put(entry));

export const deleteTranscript = (key: string): Promise<unknown> =>
  withStore('readwrite', (store) => store.delete(key));

/** Human-readable age, for telling the user how stale a restored transcript is. */
export const describeAge = (savedAt: number, now: number): string => {
  const seconds = Math.max(0, Math.round((now - savedAt) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};
